/**
 * bc-lazy-reviews — defers Judge.me's review widget until it is actually needed.
 *
 * WHY
 * Judge.me's app embed injects a `loader.js` on every page. At DOMContentLoaded
 * that loader scans the document for its trigger selectors — among them
 * `#judgeme_product_reviews`, `.jdgm-review-widget`, `[data-widget="review"]`
 * and `.jdgm-widget[data-entry-point]`. Finding any of them on a PDP it pulls
 * base.js + common.js, ~30 stylesheets and a module graph of ~52 chunks (the Vue
 * "revamp" review widget): measured at ~7.6s of mobile main-thread time, spent
 * at page load, for a widget sitting at the very bottom of the page.
 *
 * So the section renders that markup inside an inert `<template>` — template
 * content is NOT part of the document, so `querySelectorAll` cannot see it and
 * the loader's page-load pass finds nothing and loads nothing. This element
 * moves the markup into the page (and re-runs the loader) when one of these
 * happens:
 *   1. the section nears the viewport (IntersectionObserver, 900px margin);
 *   2. the user clicks a `[data-bc-reviews-trigger]` — the star badge under the
 *      PDP title links here (snippets/bc-review-stars.liquid);
 *   3. the page loaded on a reviews hash (#reviews / #judgeme /
 *      #judgeme_product_reviews) — a deep link straight to the reviews.
 *
 * After mounting, Judge.me's own code does exactly what it does today, just
 * later: nothing here reimplements or patches the widget.
 *
 * FAILURE MODE
 * The re-run works by resetting `window.jdgm._loaderExecuted` — the loader's own
 * re-entrancy guard (name read out of the live judgeme-713 loader.js: it returns
 * immediately when truthy, else sets it). If Judge.me ever renames that flag the
 * second execution becomes a no-op and the PDP review widget stays empty —
 * loud and visible, not a silent data problem. The fallback is to drop the
 * `<template>` wrapper in sections/bc-product-reviews.liquid and go back to
 * eager rendering.
 */
(() => {
  if (customElements.get('bc-lazy-reviews')) return;

  // Hashes that mean "the visitor came here for the reviews".
  const REVIEW_HASHES = ['#reviews', '#judgeme', '#judgeme_product_reviews'];

  // One loader re-run per page, no matter how many instances mount (a combined
  // listing could in principle render more than one review section); reset
  // below for a theme-editor section reload.
  let loaderRerun = false;

  // Theme editor: a section re-render swaps in a fresh, unmounted instance
  // that Judge.me knows nothing about — allow one more loader run for it.
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target && event.target.querySelector && event.target.querySelector('bc-lazy-reviews')) {
      loaderRerun = false;
    }
  });

  class BcLazyReviews extends HTMLElement {
    #observer = null;
    #onDocumentClick = null;
    #mounted = false;

    connectedCallback() {
      // No template = nothing to mount (already mounted, or the section markup
      // changed). Stay inert rather than guessing.
      if (!this.querySelector('template[data-bc-reviews-template]')) return;

      // Capture phase: the badge anchor's own default (jump to #reviews) still
      // runs; we only need the markup to exist by the time the jump lands.
      this.#onDocumentClick = (event) => {
        const target = event.target;
        if (target && target.closest && target.closest('[data-bc-reviews-trigger]')) this.#mount();
      };
      document.addEventListener('click', this.#onDocumentClick, { capture: true });

      if (REVIEW_HASHES.includes(window.location.hash)) {
        this.#mount();
        return;
      }

      // No timer backstop, deliberately. Search crawlers render with a very
      // tall viewport (Googlebot mobile ≈ 12,000 px), so the observer's
      // 900 px margin already mounts the widget for them, and any timed
      // fallback long enough to matter still lands inside a throttled
      // mobile load (measured: a 12 s post-load timer re-added all ~80
      // Judge.me requests to Lighthouse's window). Reviews for a visitor who
      // never scrolls toward them are reviews nobody was going to read.

      if ('IntersectionObserver' in window) {
        this.#observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) this.#mount();
          },
          // Generous margin: the widget needs its own fetch + paint after we
          // mount, so start it well before the section is on screen.
          { rootMargin: '900px 0px' }
        );
        this.#observer.observe(this);
      } else {
        this.#mount();
      }
    }

    disconnectedCallback() {
      this.#teardown();
    }

    #teardown() {
      if (this.#observer) {
        this.#observer.disconnect();
        this.#observer = null;
      }
      if (this.#onDocumentClick) {
        document.removeEventListener('click', this.#onDocumentClick, { capture: true });
        this.#onDocumentClick = null;
      }
    }

    /** Move the widget markup into the document, then let Judge.me find it. */
    #mount() {
      if (this.#mounted) return;
      this.#mounted = true;
      this.#teardown();

      const template = this.querySelector('template[data-bc-reviews-template]');
      const slot = this.querySelector('[data-bc-reviews-slot]') || this;
      if (template) {
        slot.appendChild(template.content.cloneNode(true));
        template.remove();
      }
      // Hook for CSS (e.g. releasing the placeholder min-height) and for
      // debugging in the browser inspector.
      this.setAttribute('data-bc-mounted', '');

      this.#runJudgeMeLoader();
    }

    /**
     * Re-execute Judge.me's own loader so it discovers the now-present widget.
     *
     * Re-running redefines `jdgm.loadScript` / `jdgm.loadCSS` with fresh dedupe
     * lists — harmless here precisely because the first run loaded nothing (no
     * trigger selector existed on the page), so there is nothing to double-load.
     * The loader collects `.jdgm-widget[data-entry-point]` synchronously at
     * execution, which is why the markup is mounted BEFORE the script is appended.
     */
    #runJudgeMeLoader() {
      if (loaderRerun) return;
      loaderRerun = true;

      const tag = document.querySelector(
        'script[src*="judgeme"][src$="/loader.js"], script[src*="/judgeme-"][src*="loader.js"]'
      );
      // No loader tag = the app embed is disabled (or Judge.me changed the URL).
      // The mounted markup is then a harmless empty shell — the same thing the
      // page rendered before this element existed.
      if (!tag) return;

      window.jdgm = window.jdgm || {};
      window.jdgm._loaderExecuted = false;

      // Each loader execution re-creates jdgm.loadScript with an EMPTY
      // requestedUrls dedupe list. On this page the first run loaded nothing,
      // but if a Judge.me element that IS a page-load trigger ever comes back
      // (a dashboard-enabled carousel / verified-count badge, or the preview
      // badge classes restored), the second run would fetch and re-evaluate
      // base.js + common.js on top of the first — duplicated handlers, quietly.
      // Carry the first run's list across: `load` fires after the loader body
      // ran (new list exists) but before its setTimeout(0) docReady handlers
      // request anything.
      const prev = window.jdgm.loadScript && window.jdgm.loadScript.requestedUrls;
      const prevUrls = Array.isArray(prev) ? prev.slice() : null;

      const script = document.createElement('script');
      script.src = tag.src;
      script.async = true;
      script.dataset.bcReviewsLoader = '';
      script.onload = () => {
        const list = window.jdgm.loadScript && window.jdgm.loadScript.requestedUrls;
        if (prevUrls && Array.isArray(list)) {
          for (const url of prevUrls) if (!list.includes(url)) list.push(url);
        }
      };
      document.head.appendChild(script);
    }
  }

  customElements.define('bc-lazy-reviews', BcLazyReviews);
})();
